import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, User, Globe, Search } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const mockAccessLogs = [
  { id: 1, userId: 1, userName: 'Administrador Principal', email: 'admin@empresa.com', timestamp: '2024-07-20 10:30:00', ipAddress: '192.168.1.1', device: 'Desktop', location: 'Madrid, España' },
  { id: 2, userId: 2, userName: 'Agencia Marketing Pro', email: 'agencia@marketing.com', timestamp: '2024-07-20 09:45:00', ipAddress: '203.0.113.45', device: 'Mobile', location: 'Barcelona, España' },
  { id: 3, userId: 1, userName: 'Administrador Principal', email: 'admin@empresa.com', timestamp: '2024-07-19 18:00:00', ipAddress: '192.168.1.1', device: 'Desktop', location: 'Madrid, España' },
  { id: 4, userId: 3, userName: 'Distribuidor Elite', email: 'distribuidor@ventas.com', timestamp: '2024-07-19 14:15:00', ipAddress: '198.51.100.22', device: 'Desktop', location: 'Valencia, España' },
  { id: 5, userId: 2, userName: 'Agencia Marketing Pro', email: 'agencia@marketing.com', timestamp: '2024-07-19 11:20:00', ipAddress: '203.0.113.45', device: 'Mobile', location: 'Barcelona, España' },
  { id: 6, userId: 4, userName: 'Usuario de Prueba', email: 'test@example.com', timestamp: '2024-07-18 08:00:00', ipAddress: '10.0.0.5', device: 'Desktop', location: 'Sevilla, España' },
];

const AccessHistoryPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [accessLogs, setAccessLogs] = useState(mockAccessLogs);
  const [searchTerm, setSearchTerm] = useState('');

  if (!isAdmin) {
    navigate('/admin'); // Redirigir si no es administrador
    return null;
  }

  const filteredLogs = accessLogs.filter(log =>
    log.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.ipAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Historial de Accesos</h2>
          <p className="text-xl text-gray-600">Monitorea los inicios de sesión y la actividad de los usuarios.</p>
        </motion.div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 mb-8 shadow-lg border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="relative flex-1 max-w-md mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar accesos por usuario, agencia, hotel o ubicación..."
              className="w-full pl-12 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-6 text-left">Usuario</th>
                  <th className="py-3 px-6 text-left">Email</th>
                  <th className="py-3 px-6 text-left">Fecha y Hora</th>
                  <th className="py-3 px-6 text-left">IP</th>
                  <th className="py-3 px-6 text-left">Dispositivo</th>
                  <th className="py-3 px-6 text-left">Ubicación</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 text-sm font-light">
                {filteredLogs.map((log) => (
                  <motion.tr
                    key={log.id}
                    className="border-b border-gray-200 hover:bg-gray-50"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <td className="py-4 px-6 text-left whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-blue-500 mr-2" />
                        <span className="font-medium">{log.userName}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-left">{log.email}</td>
                    <td className="py-4 px-6 text-left">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                        {log.timestamp}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-left">{log.ipAddress}</td>
                    <td className="py-4 px-6 text-left">{log.device}</td>
                    <td className="py-4 px-6 text-left">
                      <div className="flex items-center">
                        <Globe className="w-4 h-4 text-gray-400 mr-2" />
                        {log.location}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredLogs.length === 0 && (
            <motion.div
              className="text-center py-12 text-gray-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              No se encontraron registros de acceso que coincidan con tu búsqueda.
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AccessHistoryPage;